/**
 * TermsOfService — public legal page.
 *
 * Lives at `/terms`. Reachable without auth so Stripe can scrape it during
 * Connect onboarding (Stripe requires a public Terms URL on the business
 * profile). Same dark aesthetic as the Privacy Policy page.
 */

import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "May 24, 2026";
const SUPPORT_EMAIL = "support@countbucks.com";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="terms-of-service-page">
      <header className="border-b border-white/5 bg-black/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
            data-testid="terms-back-home"
          >
            <img src="/countbucks-icon.png" alt="CountBucks" className="w-7 h-7" />
            <span className="text-zinc-200 font-bold text-sm tracking-tight">CountBucks</span>
          </Link>
          <Link
            to="/"
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1.5 font-mono"
          >
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <div className="mb-12">
          <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">Terms of Service</h1>
          <p className="text-sm text-zinc-500 mt-3 font-mono">Last updated: {LAST_UPDATED}</p>
        </div>

        <article className="space-y-10">
          <Section title="1. Acceptance">
            <p>
              These Terms of Service (“Terms”) govern your access to and use of the CountBucks
              platform (“CountBucks”, “Service”). By creating an account, accessing the Service, or
              clicking “I agree”, you accept these Terms. If you do not agree, do not use the
              Service.
            </p>
          </Section>

          <Section title="2. The Service">
            <p>
              CountBucks is an AI-assisted business operating system that provides accounting,
              bookkeeping, invoicing, document management, banking integrations, and AI-driven
              insights. CountBucks is <strong>not</strong> a substitute for a licensed certified
              public accountant (CPA), tax attorney, or financial advisor. You are responsible for
              the accuracy of all entries and for ensuring that filings, tax obligations, and
              legally binding decisions are reviewed by qualified professionals.
            </p>
          </Section>

          <Section title="3. Accounts">
            <ul>
              <li>You must be at least 18 years old and able to enter binding contracts.</li>
              <li>You are responsible for maintaining the confidentiality of your credentials.</li>
              <li>You are responsible for all activity on your account.</li>
              <li>You must promptly notify us at <a className="text-emerald-400" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> of any suspected unauthorized access.</li>
            </ul>
          </Section>

          <Section title="4. Plans, billing, and trials">
            <p>
              Paid plans renew automatically until cancelled. Subscription fees are billed through
              Stripe in advance, are non-refundable except where required by law, and may include
              metered AI usage charges based on your plan. We may change pricing on a going-forward
              basis with at least 30 days’ notice. You can cancel at any time from your billing
              page; cancellation stops future renewals but does not refund the current period.
            </p>
          </Section>

          <Section title="5. Acceptable use">
            <p>You agree not to:</p>
            <ul>
              <li>Use CountBucks to commit fraud, launder money, or violate any law.</li>
              <li>Upload malware, exploits, or content that infringes intellectual-property rights.</li>
              <li>Attempt to reverse engineer, scrape, or disrupt the Service.</li>
              <li>Use AI features to generate misleading financial statements or impersonate any person.</li>
              <li>Resell or sublicense access to CountBucks without our written consent.</li>
            </ul>
          </Section>

          <Section title="6. Your content">
            <p>
              You retain all rights to data and content you upload to CountBucks (“Your Content”).
              You grant us a worldwide, non-exclusive license to host, copy, process, and display
              Your Content solely as necessary to operate the Service, provide AI features, and
              perform support. We will never sell Your Content or use it to train third-party AI
              models.
            </p>
          </Section>

          <Section title="7. AI features">
            <p>
              CountBucks AI uses large language models from leading AI providers. AI output may
              be inaccurate, incomplete, or outdated. You are responsible for reviewing AI
              suggestions before acting on them. AI responses are not legal, tax, or accounting
              advice.
            </p>
          </Section>

          <Section title="8. Third-party services">
            <p>
              The Service integrates with third parties (Stripe, Plaid, cloud storage providers,
              email delivery providers, AI providers). Your use of those services is governed by
              their own terms. We are not responsible for the availability, accuracy, or actions
              of any third-party service.
            </p>
          </Section>

          <Section title="9. Termination">
            <p>
              You may close your account at any time. We may suspend or terminate your account if
              you breach these Terms, fail to pay fees, or pose a security or legal risk. Upon
              termination, your access stops; you may export your data for 30 days after which it
              may be permanently deleted.
            </p>
          </Section>

          <Section title="10. Disclaimers">
            <p>
              THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND,
              EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              UNINTERRUPTED, ERROR-FREE, OR THAT AI OUTPUTS WILL BE ACCURATE.
            </p>
          </Section>

          <Section title="11. Limitation of liability">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, COUNTBUCKS WILL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, DATA,
              OR GOODWILL. OUR TOTAL LIABILITY ARISING OUT OF OR RELATED TO THE SERVICE WILL NOT
              EXCEED THE GREATER OF (A) USD $100 OR (B) THE FEES YOU PAID US IN THE 12 MONTHS
              PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
            </p>
          </Section>

          <Section title="12. Indemnification">
            <p>
              You agree to indemnify and hold CountBucks harmless from any third-party claims
              arising out of your use of the Service, Your Content, or your violation of these
              Terms.
            </p>
          </Section>

          <Section title="13. Governing law">
            <p>
              These Terms are governed by the laws of the State of Delaware, United States, without
              regard to conflict-of-law principles. Disputes will be resolved in the state or
              federal courts located in Delaware, and you consent to personal jurisdiction there.
            </p>
          </Section>

          <Section title="14. Changes">
            <p>
              We may update these Terms from time to time. Material changes will be communicated
              in-app and via email at least 30 days before they take effect. Continued use after
              the effective date means you accept the revised Terms.
            </p>
          </Section>

          <Section title="15. Contact">
            <p>
              Questions about these Terms? Email{" "}
              <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>
        </article>

        <footer className="mt-20 pt-8 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500 font-mono">
          <span>© {new Date().getFullYear()} CountBucks</span>
          <Link to="/privacy" className="hover:text-zinc-300 transition-colors" data-testid="terms-to-privacy">
            ← Privacy Policy
          </Link>
        </footer>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-semibold text-white mb-3 tracking-tight">{title}</h2>
      <div className="text-sm leading-relaxed text-zinc-300 [&_a]:underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ul]:mt-3 [&_p]:mt-3 [&_p:first-child]:mt-0 [&_strong]:text-white [&_strong]:font-semibold">
        {children}
      </div>
    </section>
  );
}
